import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  CircularProgress,
  Divider,
  Alert,
} from "@mui/material";
import { PersonOutline } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";

import heroWind from "../assets/hero-wind.jpg";
import reccLogo from "../assets/logo.png";
import evccLogo from "../assets/evcc.png";
import "./LoginPage.css";

/* ——— Tokens to match LoginPage ——— */
const TOKENS = {
  pageBg: "#FAFAFA",
  primary: "#015291",
  primaryDark: "#005695",
  inputBorder: "#8692A6",
  helperText: "#8692A6",
  labelColor: "#696F79",
  bodyText: "#494949",
  divider: "#E4E4E4",
  radius: 6,
};

const AUTH_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
const auth = axios.create({
  baseURL: AUTH_BASE,
  timeout: 200000,
  validateStatus: () => true,
});

export default function ForgotPasswordPage() {
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      height: "clamp(52px, 6vw, 64px)",
      borderRadius: TOKENS.radius,
      backgroundColor: "#fff",
      "& fieldset": { borderColor: TOKENS.inputBorder },
      "&:hover fieldset": { borderColor: TOKENS.inputBorder },
      "&.Mui-focused fieldset": { borderColor: TOKENS.primary },
      "&.Mui-focused": { boxShadow: "0 4px 10px 3px rgba(0,0,0,0.11)" },
    },
    "& input::placeholder": { color: "#696F79", opacity: 1 },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!identifier?.trim()) {
      setError("Please enter your email or RECC/EVCC number.");
      setSubmitted(false);
      return;
    }

    setError(null);
    setSubmitted(false);
    setLoading(true);

    try {
      const resp = await auth.post(
        "/auth/forgot-password",
        { identifier: identifier.trim() },
        { headers: { "X-Skip-Auth-Redirect": "1", "X-No-Auth": "1" } }
      );

      if (resp.status >= 200 && resp.status < 300) {
        setSubmitted(true);
        return;
      }

      setError(resp?.data?.message || `Request failed (HTTP ${resp.status}).`);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setIdentifier(e.target.value);
    if (error) setError(null);
    if (submitted) setSubmitted(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(460px, 50vw) 1fr" },
        background: TOKENS.pageBg,
      }}
    >
      {/* LEFT: hero (reused from Login) */}
      <Box
        className="login-hero"
        sx={{
          display: { xs: "none", md: "flex" },
          position: "relative",
          alignItems: "center",
          px: { md: 6, lg: 8 },
          backgroundImage: `linear-gradient(0deg, rgba(1,82,145,0.8), rgba(1,82,145,0.8)), url(${heroWind})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "#fff",
          borderRight: "1px solid #E6EDF4",
        }}
      >
        <Box
          sx={{
            maxWidth: 520,
            ml: { md: "clamp(32px, 6vw, 90px)" },
            mr: { md: 2 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { md: 28, lg: 30 },
              lineHeight: "36px",
              mb: { md: 3, lg: "36px" },
            }}
          >
            Forgot your password?
          </Typography>
          <Typography
            sx={{
              fontSize: { md: 16, lg: 20 },
              lineHeight: { md: "28px", lg: "38px" },
            }}
          >
            Enter your email or membership number. If it matches an account,
            we&apos;ll send instructions to reset your password.
          </Typography>
        </Box>
        <Box className="corner-bracket" />
      </Box>

      {/* RIGHT: form column */}
      <Box
        sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center" }}
      >
        <Container
          maxWidth="md"
          sx={{
            px: { xs: 3, sm: 4, md: 6, lg: 8 },
            pt: { xs: 4, md: 8 },
            pb: { xs: 6, md: 10 },
            width: "100%",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 540 }}>
            {/* Logos row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: { xs: 3, md: 4 },
              }}
            >
              <Box
                component="img"
                src={reccLogo}
                alt="RECC"
                sx={{ height: { xs: 28, md: 34, lg: 40 } }}
              />
              <Box
                sx={{
                  width: "1px",
                  height: { xs: 28, md: 34, lg: 40 },
                  bgcolor: "#CACACA",
                  mx: 1,
                }}
              />
              <Box
                component="img"
                src={evccLogo}
                alt="EVCC"
                sx={{ height: { xs: 28, md: 34, lg: 40 } }}
              />
            </Box>

            <Typography
              variant="h4"
              sx={{ fontWeight: 800, mb: 1, fontSize: { xs: 26, md: 30 } }}
            >
              Reset your password
            </Typography>
            <Typography
              sx={{
                color: TOKENS.helperText,
                mb: 2,
                fontSize: { xs: 16, md: 18 },
                lineHeight: { xs: "26px", md: "28px" },
              }}
            >
              We’ll email you a link to set a new password.
            </Typography>
            <Divider
              sx={{ mb: { xs: 3, md: 3.5 }, borderColor: TOKENS.divider }}
            />

            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ width: "100%" }}
            >
              {submitted && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  If an account exists for the provided details, we’ve sent reset
                  instructions. Please check your inbox and spam folder.
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Typography
                sx={{
                  color: TOKENS.labelColor,
                  fontWeight: 500,
                  mb: 1.5,
                  fontSize: { xs: 14, md: 16 },
                }}
              >
                Email or RECC/EVCC number
              </Typography>
              <TextField
                placeholder="Email Address"
                value={identifier}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: false }}
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: "6px" }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 2,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PersonOutline
                          sx={{ fontSize: 22, color: TOKENS.primary }}
                        />
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                disabled={loading}
                variant="contained"
                fullWidth
                sx={{
                  mt: 4,
                  height: "clamp(48px, 6vw, 64px)",
                  borderRadius: TOKENS.radius,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: 16, md: 18 },
                  backgroundColor: TOKENS.primary,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: TOKENS.primaryDark,
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Send reset link"
                )}
              </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: TOKENS.bodyText }}>
                Remembered your password?{" "}
                <Link
                  component={RouterLink}
                  to="/"
                  underline="always"
                  sx={{ color: TOKENS.primary, fontWeight: 600 }}
                >
                  Back to Login
                </Link>
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

/*
Route:
  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
*/
